// What the database's health watch (CER-69) and Home's warnings (CER-80)
// hand the apps, read field by field: a shape the apps do not expect is an
// error to show, never a warning quietly dropped. The words both apps show
// are made here, once.

/** home_warnings(): the signed-in user's devices and reminders to come; problems now, for an owner only (null otherwise). */
export interface HomeWarnings {
  devices: number;
  upcoming_reminders: number;
  problems: number | null;
}

/** One scheduled job on the owner's status page (system_health). Every time is ISO text, or null when none is recorded. */
export interface HealthJob {
  name: string;
  label: string;
  schedule: string;
  last_run_at: string | null;
  /** pg_cron's word for its last run: succeeded, failed, running, starting… */
  last_status: string | null;
  /** The last completed run of the function the job wakes (its heartbeat). */
  last_success_at: string | null;
  last_wakeup_at: string | null;
  /** The answer to the newest wake-up that has one: "HTTP 200", "HTTP 500", "timeout", "network error". */
  last_answer: string | null;
  last_answer_at: string | null;
}

export interface HealthProblem {
  key: string;
  summary: string;
  /** When the watch first saw it; null when the watch has not run since it began. */
  since: string | null;
}

export interface HealthEpisode {
  id: number;
  key: string;
  summary: string;
  opened_at: string;
  last_seen_at: string;
  cleared_at: string | null;
}

export interface HealthAlert {
  health_problem_id: number;
  alert_event: string;
  created_at: string;
  delivery_status: string | null;
  status_at: string | null;
  sent_at: string | null;
}

export interface FilingFailureSummary {
  category: string;
  count: number;
  latest_at: string;
}

export interface DeliverySummary {
  status: string;
  count: number;
}

export interface HealthCalendar {
  ordinal: number;
  kind: 'caldav' | 'outlook';
  needs_reconnect: boolean;
  last_sync_at: string | null;
  has_error: boolean;
}

export interface SystemHealth {
  checked_at: string;
  problems: HealthProblem[];
  jobs: HealthJob[];
  episodes: HealthEpisode[];
  alerts: HealthAlert[];
  filing_failures: FilingFailureSummary[];
  deliveries: DeliverySummary[];
  calendars: HealthCalendar[];
}

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isPositiveInteger = (value: unknown): value is number => isCount(value) && value > 0;
const isText = (value: unknown): value is string => typeof value === 'string';
const isTextOrNull = (value: unknown): value is string | null => value === null || typeof value === 'string';
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/** home_warnings()'s answer; throws when it is not one. */
export function parseHomeWarnings(data: unknown): HomeWarnings {
  if (!isRecord(data) || !isCount(data.devices) || !isCount(data.upcoming_reminders) || !(data.problems === null || isCount(data.problems))) {
    throw new Error('the warnings for Home did not arrive');
  }
  return { devices: data.devices, upcoming_reminders: data.upcoming_reminders, problems: data.problems };
}

const JOB_FIELDS = ['last_run_at', 'last_status', 'last_success_at', 'last_wakeup_at', 'last_answer', 'last_answer_at'] as const;

/** system_health()'s answer: null for anyone who is not an owner; throws when it is neither. */
export function parseSystemHealth(data: unknown): SystemHealth | null {
  if (data === null) return null;
  const malformed = () => new Error('the system status did not arrive');
  if (!isRecord(data) || !isText(data.checked_at) || !Array.isArray(data.problems) || !Array.isArray(data.jobs)
    || !Array.isArray(data.episodes) || !Array.isArray(data.alerts) || !Array.isArray(data.filing_failures)
    || !Array.isArray(data.deliveries) || !Array.isArray(data.calendars)) throw malformed();
  const problems = data.problems.map((p): HealthProblem => {
    if (!isRecord(p) || !isText(p.key) || !isText(p.summary) || !isTextOrNull(p.since)) throw malformed();
    return { key: p.key, summary: p.summary, since: p.since };
  });
  const jobs = data.jobs.map((j): HealthJob => {
    if (!isRecord(j) || !isText(j.name) || !isText(j.label) || !isText(j.schedule) || JOB_FIELDS.some((f) => !isTextOrNull(j[f]))) {
      throw malformed();
    }
    return {
      name: j.name,
      label: j.label,
      schedule: j.schedule,
      last_run_at: j.last_run_at as string | null,
      last_status: j.last_status as string | null,
      last_success_at: j.last_success_at as string | null,
      last_wakeup_at: j.last_wakeup_at as string | null,
      last_answer: j.last_answer as string | null,
      last_answer_at: j.last_answer_at as string | null,
    };
  });
  const episodes = data.episodes.map((e): HealthEpisode => {
    if (!isRecord(e) || !isPositiveInteger(e.id) || !isText(e.key) || !isText(e.summary)
      || !isText(e.opened_at) || !isText(e.last_seen_at) || !isTextOrNull(e.cleared_at)) throw malformed();
    return { id: e.id, key: e.key, summary: e.summary, opened_at: e.opened_at, last_seen_at: e.last_seen_at, cleared_at: e.cleared_at };
  });
  const alerts = data.alerts.map((a): HealthAlert => {
    if (!isRecord(a) || !isPositiveInteger(a.health_problem_id) || !isText(a.alert_event) || !isText(a.created_at)
      || !isTextOrNull(a.delivery_status) || !isTextOrNull(a.status_at) || !isTextOrNull(a.sent_at)) throw malformed();
    return {
      health_problem_id: a.health_problem_id,
      alert_event: a.alert_event,
      created_at: a.created_at,
      delivery_status: a.delivery_status,
      status_at: a.status_at,
      sent_at: a.sent_at,
    };
  });
  const filingFailures = data.filing_failures.map((f): FilingFailureSummary => {
    if (!isRecord(f) || !isText(f.category) || !isCount(f.count) || !isText(f.latest_at)) throw malformed();
    return { category: f.category, count: f.count, latest_at: f.latest_at };
  });
  const deliveries = data.deliveries.map((d): DeliverySummary => {
    if (!isRecord(d) || !isText(d.status) || !isCount(d.count)) throw malformed();
    return { status: d.status, count: d.count };
  });
  const calendars = data.calendars.map((c): HealthCalendar => {
    if (!isRecord(c) || !isPositiveInteger(c.ordinal) || (c.kind !== 'caldav' && c.kind !== 'outlook')
      || typeof c.needs_reconnect !== 'boolean' || !isTextOrNull(c.last_sync_at) || typeof c.has_error !== 'boolean') throw malformed();
    return {
      ordinal: c.ordinal,
      kind: c.kind,
      needs_reconnect: c.needs_reconnect,
      last_sync_at: c.last_sync_at,
      has_error: c.has_error,
    };
  });
  return {
    checked_at: data.checked_at,
    problems,
    jobs,
    episodes,
    alerts,
    filing_failures: filingFailures,
    deliveries,
    calendars,
  };
}

/**
 * What Home says, if anything: that reminders are coming and no device will
 * receive them (CER-80), and, for an owner, that something is wrong now
 * (CER-69). Null for each that does not apply.
 */
export function homeWarningText(warnings: HomeWarnings): { noDevice: string | null; problems: string | null } {
  const n = warnings.upcoming_reminders;
  const p = warnings.problems ?? 0;
  return {
    noDevice:
      warnings.devices === 0 && n > 0
        ? `No device receives your notifications, so your ${n === 1 ? 'upcoming reminder' : `${n} upcoming reminders`} will not be sent.`
        : null,
    problems: p > 0 ? `Cerebro Diem has ${p === 1 ? 'a problem' : `${p} problems`} right now. System status in Settings says what.` : null,
  };
}

/**
 * What is said wherever a reminder is set (CER-150): the account has no
 * device, so the reminder will be recorded as not sent. Null when a device
 * will receive it. Unlike Home's warning it does not wait for a reminder to
 * be queued: it is read while one is being set.
 */
export function noDeviceReminderNote(warnings: HomeWarnings): string | null {
  return warnings.devices === 0 ? 'No device receives your notifications, so this reminder will not be sent.' : null;
}

/**
 * The lines a job shows on the status page, with `ago` turning an ISO time
 * into words ("3 min ago"). The function lines appear only for a job that
 * wakes a function (it has been woken, or has a heartbeat).
 */
export function jobStatusLines(job: HealthJob, ago: (iso: string) => string): string[] {
  const lines = [job.last_run_at ? `Last run ${ago(job.last_run_at)}: ${job.last_status ?? 'no result recorded'}` : 'No run recorded'];
  if (job.last_wakeup_at !== null || job.last_success_at !== null) {
    lines.push(job.last_success_at ? `Last completed run ${ago(job.last_success_at)}` : 'No completed run recorded');
    if (job.last_answer && job.last_answer_at) lines.push(`Last answer: ${job.last_answer} (${ago(job.last_answer_at)})`);
  }
  return lines;
}

/** A job whose last run failed, or which answered its last wake-up with an error, is shown as failing. */
export function jobFailing(job: HealthJob): boolean {
  return job.last_status === 'failed' || (job.last_answer !== null && job.last_answer !== 'HTTP 200');
}
